/**
 * PDP interactions — loaded via theme.liquid on product templates.
 * Kept outside {% javascript %} so enquire / lightbox / CTA always bind.
 */
(() => {
  'use strict';

  const BP = (window.BP = window.BP || {});
  if (BP._productPageInit) return;
  BP._productPageInit = true;

  /* ------------------------------------------------------------------ */
  /* Description teaser — only if Liquid left full HTML to clip          */
  /* ------------------------------------------------------------------ */
  const clipDescription = () => {
    const root = document.getElementById('ProductMain');
    if (!root) return;
    const teaser = root.querySelector('[data-desc-teaser]');
    const seeMore = root.querySelector('[data-desc-see-more]');
    if (!teaser) return;

    const reveal = (showSeeMore) => {
      teaser.classList.remove('is-pending-clip');
      if (seeMore) seeMore.hidden = !showSeeMore;
    };

    try {
      const target = Number(teaser.getAttribute('data-words') || 90);
      const walker = document.createTreeWalker(teaser, NodeFilter.SHOW_TEXT, null);
      const textNodes = [];
      let node;
      while ((node = walker.nextNode())) {
        if (node.nodeValue && node.nodeValue.trim()) textNodes.push(node);
      }

      const wordCounts = [];
      let wordCount = 0;
      textNodes.forEach((textNode) => {
        let local = 0;
        textNode.nodeValue.split(/(\s+)/).forEach((part) => {
          if (part && !/^\s+$/.test(part)) local += 1;
        });
        wordCounts.push(local);
        wordCount += local;
      });

      if (wordCount <= target) {
        reveal(false);
        return;
      }

      let running = 0;
      let cutNode = null;
      let cutWordIndex = 0;
      let targetNodeIndex = 0;
      for (let i = 0; i < textNodes.length; i += 1) {
        const next = running + wordCounts[i];
        if (next >= target) {
          cutNode = textNodes[i];
          cutWordIndex = target - running;
          targetNodeIndex = i;
          break;
        }
        running = next;
      }

      if (!cutNode) {
        reveal(true);
        return;
      }

      const endsSentence = (token) => /[.!?\u2026]["'\u00bb)\]]*$/.test(token);

      const findNearestCut = (textNode, preferredWordIndex) => {
        const parts = textNode.nodeValue.split(/(\s+)/);
        const wordIdxs = [];
        parts.forEach((part, idx) => {
          if (part && !/^\s+$/.test(part)) wordIdxs.push(idx);
        });
        if (!wordIdxs.length) return textNode.nodeValue.length;

        let before = -1;
        let after = -1;
        const pref = Math.min(Math.max(preferredWordIndex, 1), wordIdxs.length) - 1;
        for (let w = 0; w <= pref; w += 1) {
          if (endsSentence(parts[wordIdxs[w]])) before = w;
        }
        for (let w = pref; w < wordIdxs.length; w += 1) {
          if (endsSentence(parts[wordIdxs[w]])) {
            after = w;
            break;
          }
        }

        let chosen = pref;
        if (before >= 0 && after >= 0) chosen = after - pref < pref - before ? after : before;
        else if (after >= 0) chosen = after;
        else if (before >= 0) chosen = before;

        let end = 0;
        for (let i = 0; i <= wordIdxs[chosen]; i += 1) end += parts[i].length;
        return end;
      };

      let cutAt = findNearestCut(cutNode, cutWordIndex);
      const rawParts = cutNode.nodeValue.slice(0, cutAt).trim();
      if (!/[.!?\u2026]["'\u00bb)\]]*$/.test(rawParts)) {
        let wordsAhead = 0;
        let found = false;
        for (let i = targetNodeIndex; i < textNodes.length && wordsAhead < 40; i += 1) {
          const tn = textNodes[i];
          const parts = tn.nodeValue.split(/(\s+)/);
          let endPos = 0;
          for (let p = 0; p < parts.length; p += 1) {
            endPos += parts[p].length;
            if (!parts[p] || /^\s+$/.test(parts[p])) continue;
            wordsAhead += 1;
            if (endsSentence(parts[p])) {
              cutNode = tn;
              cutAt = endPos;
              found = true;
              break;
            }
            if (wordsAhead >= 40) break;
          }
          if (found) break;
        }
      }

      cutNode.nodeValue = cutNode.nodeValue.slice(0, cutAt);
      let current = cutNode;
      while (current && current !== teaser) {
        while (current.nextSibling) current.parentNode.removeChild(current.nextSibling);
        current = current.parentNode;
      }
      teaser.querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6').forEach((el) => {
        if (!el.textContent || !el.textContent.trim()) el.remove();
      });
      reveal(true);
    } catch (err) {
      reveal(true);
    }
  };

  /* ------------------------------------------------------------------ */
  /* Request a quote panel                                               */
  /* ------------------------------------------------------------------ */
  const initEnquiry = () => {
    const root = document.getElementById('ProductMain');
    const panel = document.getElementById('ProductEnquiry');
    if (!root || !panel || panel.dataset.bpEnquiryBound === 'true') return;
    panel.dataset.bpEnquiryBound = 'true';

    const form = panel.querySelector('form.product-enquiry__form, form[action*="/contact"]');
    const protectCaptcha = () => {
      if (!form) return;
      form.setAttribute('data-shopify-captcha', 'true');
      try {
        if (window.Shopify && window.Shopify.captcha && window.Shopify.captcha.protect) {
          window.Shopify.captcha.protect(form);
        }
      } catch (err) {}
    };

    const open = () => {
      panel.hidden = false;
      document.body.classList.add('product-enquiry-on');
      requestAnimationFrame(() => panel.classList.add('is-open'));
      protectCaptcha();
      try {
        panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } catch (err) {}
    };

    const close = () => {
      panel.classList.remove('is-open');
      document.body.classList.remove('product-enquiry-on');
      const done = () => {
        if (panel.classList.contains('is-open')) return;
        panel.hidden = true;
      };
      panel.addEventListener('transitionend', done, { once: true });
      setTimeout(done, 360);
    };

    root.addEventListener('click', (e) => {
      if (e.target.closest('[data-enquire-open]')) {
        e.preventDefault();
        open();
      } else if (e.target.closest('[data-enquire-close]')) {
        e.preventDefault();
        close();
      }
    });

    if (panel.querySelector('.product-enquiry__success')) open();
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('enquire') === '1' || window.location.hash === '#ProductEnquiry') open();
    } catch (err) {}
  };

  /* ------------------------------------------------------------------ */
  /* Lightbox                                                            */
  /* ------------------------------------------------------------------ */
  const initLightbox = () => {
    const dialog = document.querySelector('[data-product-lightbox]');
    if (!dialog || dialog.dataset.bpLightboxBound === 'true') return;
    dialog.dataset.bpLightboxBound = 'true';

    const img = dialog.querySelector('[data-lightbox-image]');
    const counter = dialog.querySelector('[data-lightbox-counter]');
    const prevBtn = dialog.querySelector('[data-lightbox-prev]');
    const nextBtn = dialog.querySelector('[data-lightbox-next]');
    const sourcesEl = document.querySelector('[data-lightbox-sources]');
    let sources = [];
    try {
      sources = JSON.parse(sourcesEl && sourcesEl.textContent ? sourcesEl.textContent : '[]');
    } catch (err) {
      sources = [];
    }
    if (!sources.length || !img) return;

    let index = 0;

    const render = () => {
      const item = sources[index];
      if (!item) return;
      img.src = item.src;
      img.alt = item.alt || '';
      if (counter) {
        counter.hidden = sources.length < 2;
        counter.textContent = index + 1 + ' / ' + sources.length;
      }
      const multi = sources.length > 1;
      if (prevBtn) prevBtn.hidden = !multi;
      if (nextBtn) nextBtn.hidden = !multi;
    };

    const openAt = (i) => {
      index = ((i % sources.length) + sources.length) % sources.length;
      render();
      if (typeof dialog.showModal === 'function') {
        if (!dialog.open) dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
    };

    const step = (delta) => {
      index = (index + delta + sources.length) % sources.length;
      render();
    };

    document.addEventListener('click', (e) => {
      const el = e.target.closest('[data-lightbox-open]');
      if (!el) return;
      e.preventDefault();
      const i = Number(el.getAttribute('data-lightbox-index') || 0);
      openAt(Number.isFinite(i) ? i : 0);
    });

    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        step(-1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        step(1);
      });
    }

    const closeBtn = dialog.querySelector('[data-lightbox-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        if (typeof dialog.close === 'function') dialog.close();
        else dialog.removeAttribute('open');
      });
    }

    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) {
        if (typeof dialog.close === 'function') dialog.close();
        else dialog.removeAttribute('open');
      }
    });

    dialog.addEventListener('keydown', (e) => {
      if (!dialog.open) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        step(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        step(1);
      }
    });

    let touchX = null;
    dialog.addEventListener(
      'touchstart',
      (e) => {
        touchX = e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientX : null;
      },
      { passive: true }
    );
    dialog.addEventListener(
      'touchend',
      (e) => {
        if (touchX == null) return;
        const x = e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientX : null;
        if (x == null) return;
        const dx = x - touchX;
        touchX = null;
        if (Math.abs(dx) < 40) return;
        step(dx < 0 ? 1 : -1);
      },
      { passive: true }
    );
  };

  /* ------------------------------------------------------------------ */
  /* 3D Configure — scroll + ask section to unlock/load                  */
  /* ------------------------------------------------------------------ */
  const initConfiguratorCta = () => {
    if (document.documentElement.dataset.bpCfgCtaBound === 'true') return;
    document.documentElement.dataset.bpCfgCtaBound = 'true';

    document.addEventListener('click', (e) => {
      const link = e.target.closest('[data-scroll-to-configurator]');
      if (!link) return;
      if (link.dataset.bpScrollBound === 'true') return;
      const target = document.getElementById('ProductConfigurator');
      if (!target) return;
      e.preventDefault();
      try {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (err) {
        window.location.hash = 'ProductConfigurator';
      }
      target.dispatchEvent(new CustomEvent('bp:configurator:open', { bubbles: true }));
      try {
        if (history.replaceState) history.replaceState(null, '', '#ProductConfigurator');
      } catch (err) {}
    });
  };

  /* ------------------------------------------------------------------ */
  /* Variant → configurator / price (mirrors quote-cart helpers)          */
  /* ------------------------------------------------------------------ */
  const initVariantSync = () => {
    const root = document.querySelector('[data-product-form]');
    if (!root || root.dataset.bpVariantInit === 'true') return;
    root.dataset.bpVariantInit = 'true';

    const emit = () => {
      const optionSelects = Array.prototype.slice
        .call(root.querySelectorAll('[data-option-index]'))
        .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex));
      const idSelect = root.querySelector('select[name="id"], [name="id"]');
      if (!idSelect) return;

      if (optionSelects.length && idSelect.tagName === 'SELECT') {
        const selected = optionSelects.map((s) => s.value);
        const match = Array.prototype.slice.call(idSelect.options).find((opt) => {
          try {
            const opts = JSON.parse(opt.dataset.options || '[]');
            return opts.length === selected.length && opts.every((v, i) => v === selected[i]);
          } catch (err) {
            return false;
          }
        });
        if (match) idSelect.value = match.value;
      }

      const option = idSelect.tagName === 'SELECT' ? idSelect.options[idSelect.selectedIndex] : idSelect;
      if (!option) return;

      root.dataset.variantBound = 'true';

      const priceWrap = root.querySelector('[data-product-price-wrap]');
      const priceEl = root.querySelector('[data-product-price]');
      if (priceWrap && option.dataset.showPrice === 'true') {
        priceWrap.hidden = false;
        const keepsFromLabel = priceWrap.querySelector('.product-main__price-label, .price__from');
        if (priceEl && option.dataset.priceFormatted && !keepsFromLabel) {
          priceEl.textContent = option.dataset.priceFormatted;
        }
      } else if (priceWrap && option.dataset.showPrice === 'false') {
        priceWrap.hidden = true;
      }

      const skuInput = root.querySelector('input[name="contact[sku]"]');
      if (skuInput && option.dataset.sku) skuInput.value = option.dataset.sku;

      const productUrl = root.getAttribute('data-product-configurator-url') || '';
      const configuratorUrl = (option.dataset.configuratorUrl || productUrl || '').trim();
      const configuratorGetLead = option.dataset.configuratorGetLead !== 'false';
      const ctaWrap = root.querySelector('[data-configurator-cta-wrap]');
      const priceNote = root.querySelector('[data-configurator-price-note]');
      if (ctaWrap) ctaWrap.hidden = !configuratorUrl;
      if (priceNote) priceNote.hidden = !configuratorUrl;

      document.dispatchEvent(
        new CustomEvent('bp:variant:change', {
          detail: {
            variantId: option.value || idSelect.value,
            configuratorUrl,
            configuratorGetLead,
            form: root,
          },
        })
      );
    };

    root.addEventListener('change', (e) => {
      if (e.target.matches('[data-option-index], [name="id"]')) emit();
    });
    root.addEventListener('click', (e) => {
      const swatch = e.target.closest('[data-swatch-value]');
      if (!swatch) return;
      /* swatch UIs usually sync the select then fire change — emit after tick */
      setTimeout(emit, 0);
    });
    emit();
  };

  /* Force-reveal product sections (avoid scroll-reveal opacity:0 stuck) */
  const revealProductSections = () => {
    document.querySelectorAll('#MainContent > .shopify-section').forEach((section) => {
      section.classList.add('is-revealed');
      section.querySelectorAll('.scroll-reveal, .scroll-reveal-item').forEach((el) => {
        el.classList.add('is-revealed');
      });
    });
  };

  const boot = () => {
    revealProductSections();
    clipDescription();
    initEnquiry();
    initLightbox();
    initConfiguratorCta();
    initVariantSync();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
